/**
 * Per-master service prices in the booking widget.
 *
 * The catalogue (`/public/services/service_categories`) sends each service's own
 * price plus, per master in `staff[]`, that master's price (`price_min`/`price_max`,
 * null = the master charges the service price). ServiceRegistry keeps the latter as
 * `staffPrices`. Backend: docs/codebase/services-staff/README.md §2.1 (boona repo).
 */
import type { ServiceItem } from "./ServiceRegistry";

export type PriceRange = { min: number; max: number };
/** `from`: the master is not chosen yet and masters charge different prices. */
export type DisplayPrice = PriceRange & { from: boolean };

type PricedService = Pick<ServiceItem, "price_min" | "price_max"> &
  Partial<Pick<ServiceItem, "staffPrices" | "staffIds">>;

function servicePrice(service: PricedService): PriceRange {
  return { min: Number(service.price_min ?? 0) || 0, max: Number(service.price_max ?? 0) || 0 };
}

/** The price a master charges for a service: their own, else the service's. */
export function masterPrice(service: PricedService, masterId: number | null | undefined): PriceRange {
  const own = masterId ? service.staffPrices?.[masterId] : undefined;
  return own ? { min: own.min, max: own.max } : servicePrice(service);
}

/**
 * The price the widget shows for a service.
 * - A master is chosen → that master's price.
 * - No master yet → the service price; when it is 0/empty and masters have their own
 *   prices, the range over the priced masters who can be booked for it ("from 1 700").
 */
export function displayPrice(service: PricedService, masterId: number | null | undefined): DisplayPrice {
  if (masterId) return { ...masterPrice(service, masterId), from: false };

  const own = servicePrice(service);
  if (own.min > 0 || own.max > 0) return { ...own, from: false };

  const bookable = service.staffIds ? new Set(service.staffIds.map(Number)) : null;
  const priced = Object.entries(service.staffPrices ?? {})
    .filter(([id]) => !bookable || bookable.has(Number(id)))
    .map(([, p]) => p)
    .filter((p) => p.max > 0);
  if (!priced.length) return { ...own, from: false };

  const min = Math.min(...priced.map((p) => (p.min > 0 ? p.min : p.max)));
  const max = Math.max(...priced.map((p) => p.max));
  return { min, max, from: min !== max };
}

/**
 * Label for a price: "from 1 700" (via `from`), "1 500 - 1 800" for a range, else the
 * single price. `format` renders a number in the viewer's locale.
 */
export function priceLabel(
  price: DisplayPrice,
  format: (n: number) => string,
  from: (formatted: string) => string,
): string {
  if (price.from) return from(format(price.min));
  const ranged = price.min > 0 && price.max > 0 && price.min !== price.max;
  if (ranged) return `${format(price.min)} - ${format(price.max)}`;
  return format(price.min > 0 ? price.min : price.max);
}
